/**
 * bb_daily_stats(OPレポート＋対応履歴の BB ID 単位の値)→ daily_summary/{date}.staff[] への変換。
 *  - OPレポートから: 稼働(受付・通話・後処理・ログイン・休憩・離席)、通話回数
 *  - 対応履歴から: 取得件数・時間帯別・割振会社(業務コード)
 *  - オペレーターマスタから: 人・出勤形態・報酬区分・架電内容(対応履歴が無い日の割振は人の架電会社=1行)
 *  - 実稼働: 在宅・管理者 = BB稼働(受付+通話+後処理)。出勤(品川・タイミー)= 拘束(ログイン時間を0.5h丸め)− 休憩(休憩時間を0.5h丸め)(D-56)。
 *    勤怠を手で直した日(shinagawa_kintai)はその値
 *  - 報酬(pay)は「参考値」(D-27: BlueBean の稼働だけで人件費を確定しない。確定値は納品件数の入力で持つ)
 * 1行 = BB ID × 割振会社。同じ BB ID が1日に複数の業務(会社)へ架電した場合は業務ごとに行を分け、
 * OPレポートの稼働・通話回数は取得件数が最も多い業務の行に付ける(按分は P-19e 保留。警告を出す)。
 */
import { commuteAmount } from '@core/lib/commute';
import { personKey } from './commuteSettle';
import { commuteOn, priceOn, wageOn, type Company, type DailySummary, type DateStr, type Kintai, type Operator, type StaffDaily } from '@core/lib/types';
import { HOUR_BANDS } from '@core/lib/types';
import type { ActivityStat, DailySummaryBuildResult, ImportIssue, ParsedOpReportRow, StaffSource, UnknownBbIdPolicy } from './types';
import { normalizeBbId } from './opReport';

const MANAGER_HOURLY = 1300; // 管理者の参考時給(現行シート)

/**
 * 0.5時間単位に丸める(最も近い30分。8.54→8.5、3.64→3.5、休憩 0:19→0.5、0:31→0.5、0:45→1.0、0:10→0)。
 * 現行は担当者がオペレーターレポートの休憩時間を見て30分単位で入れており、31分なら0.5にしていた(2026-09-10 発注者)
 */
export const round05 = (h: number): number => {
  const v = Math.round(h * 2) / 2;
  return v <= 0 ? 0 : v; // 0 のとき -0 にしない
};

/** 出勤(品川・タイミー)の 拘束・休憩・実稼働。勤怠の手修正があればそれ、無ければ BlueBean のログイン・休憩からの自動値 */
export function shukkinHours(loginSec: number, breakSec: number, kintai?: Pick<Kintai, 'shift_hours' | 'break_hours'> | null): { shift: number; brk: number; worked: number; source: 'auto' | 'manual' } {
  const shift = kintai ? kintai.shift_hours : round05(loginSec / 3600);
  const brk = kintai ? kintai.break_hours : round05(breakSec / 3600);
  return { shift, brk, worked: Math.max(0, Math.round((shift - brk) * 100) / 100), source: kintai ? 'manual' : 'auto' };
}

export type DailySummaryBuildInput = {
  date: DateStr;
  importId: string;
  /** OPレポートの正規化行(1 BB ID = 1行) */
  opRows: ParsedOpReportRow[];
  /** 対応履歴の集約値: bb_id → 業務コード → 集約 */
  activity: Map<string, Map<string, ActivityStat>>;
  /** 出勤の勤怠の手修正: bb_id → 勤怠 */
  kintai?: Map<string, Kintai>;
  operators: Operator[];
  companies: Company[];
  policy: UnknownBbIdPolicy;
};

/** 参考の報酬額(現行シートの式)。確定値ではない */
function referencePay(s: Omit<StaffDaily, 'pay'>, loginSec: number): number {
  const workSec = s.reception_sec + s.talk_sec + s.after_sec;
  if (s.work_style === 'kanri') return Math.round((MANAGER_HOURLY * loginSec) / 3600);
  if (s.pay_type === 'commission') return s.wage * s.appointments;
  if (s.work_style === 'shinagawa' || s.work_style === 'timee') return Math.round(s.wage * s.worked_hours) + s.commute;
  return Math.round((s.wage * workSec) / 3600);
}

const ZERO_OP = { reception_sec: 0, talk_sec: 0, after_sec: 0, login_sec: 0, break_sec: 0, away_sec: 0, talk_count: 0 };

export function buildDailySummary(input: DailySummaryBuildInput): DailySummaryBuildResult {
  const { date, opRows, activity, operators, companies, policy } = input;
  const kintai = input.kintai ?? new Map<string, Kintai>();
  const issues: ImportIssue[] = [];
  const trace: Record<string, StaffSource> = {};
  const accounts = new Map<string, { op: Operator; account: Operator['bb_accounts'][number] }>();
  for (const op of operators) for (const a of op.bb_accounts) accounts.set(normalizeBbId(a.bb_id), { op, account: a });
  const opByBb = new Map(opRows.map((r) => [normalizeBbId(r.bb_id), r]));

  const bbIds = [...new Set([...opByBb.keys(), ...activity.keys()])].sort();
  const staff: StaffDaily[] = [];
  const unknown: string[] = [];
  // 交通費(P-38)は品川のみ・1人1日1回。同じ人が品川の登録を2つ持つ日は実稼働を合算して判定するので、先に人ごとの実稼働を出す
  const shinagawaHours = new Map<string, number>();
  for (const bbId of bbIds) {
    const hit = accounts.get(bbId);
    if (!hit || hit.account.work_style !== 'shinagawa') continue;
    const o = opByBb.get(bbId);
    const sh = shukkinHours(o?.login_sec ?? 0, o?.break_sec ?? 0, o ? kintai.get(bbId) : null);
    const key = personKey({ name: hit.op.name, op_id: hit.op.id });
    shinagawaHours.set(key, (shinagawaHours.get(key) ?? 0) + (o ? sh.worked : 0));
  }
  const commuteGiven = new Set<string>();
  const multi: string[] = [];
  const noOp: string[] = [];

  for (const bbId of bbIds) {
    const hit = accounts.get(bbId);
    const opRow = opByBb.get(bbId);
    const acts = [...(activity.get(bbId)?.values() ?? [])].sort((a, b) => b.appointments - a.appointments || a.business_code.localeCompare(b.business_code));
    if (!hit) {
      unknown.push(`${bbId}(${opRow?.display_name ?? '対応履歴のみ'})`);
      continue;
    }
    const { op, account } = hit;
    const isShukkin = account.work_style === 'shinagawa' || account.work_style === 'timee';
    // 交通費は品川だけ(タイミー・在宅は対象外。P-38)。金額はその日の実稼働で 0/30/50%、月100時間以上なら後で70%に再計算(commuteSettle)
    const personKeyOf = personKey({ name: op.name, op_id: op.id });
    const commuteBase = account.work_style === 'shinagawa' && !commuteGiven.has(personKeyOf) ? commuteOn(op, date) : 0;
    const commute = commuteAmount(commuteBase, shinagawaHours.get(personKeyOf) ?? 0, 0);
    if (account.work_style === 'shinagawa') commuteGiven.add(personKeyOf);
    if (!opRow && acts.length > 0) noOp.push(bbId);
    if (acts.length > 1) multi.push(`${bbId}(${acts.map((a) => `${a.business_code || '業務なし'} ${a.appointments}件`).join('・')})`);

    // 行の単位: 対応履歴があれば業務(会社)ごと、無ければマスタの架電会社で1行
    const targets: { company_id: string | null; act: ActivityStat | null; withOp: boolean }[] =
      acts.length > 0 ? acts.map((a, i) => ({ company_id: a.company_id, act: a, withOp: i === 0 })) : [{ company_id: account.company_id, act: null, withOp: true }];

    targets.forEach((t, i) => {
      const o = t.withOp && opRow ? opRow : null;
      const reception = o?.reception_sec ?? ZERO_OP.reception_sec;
      const talk = o?.talk_sec ?? ZERO_OP.talk_sec;
      const after = o?.after_sec ?? ZERO_OP.after_sec;
      const login = o?.login_sec ?? ZERO_OP.login_sec;
      const brk = o?.break_sec ?? ZERO_OP.break_sec;
      const bbHours = Math.round(((reception + talk + after) / 3600) * 100) / 100;
      // 出勤(品川・タイミー)は 拘束−休憩、それ以外は BB稼働
      const sh = isShukkin && account.work_style !== 'kanri' ? shukkinHours(login, brk, o ? kintai.get(bbId) : null) : null;
      const base: Omit<StaffDaily, 'pay'> = {
        op_id: op.id,
        op_code: op.op_code,
        name: op.name,
        bb_id: account.bb_id,
        work_style: account.work_style,
        pay_type: account.pay_type,
        business: account.business,
        company_id: t.company_id,
        wage: wageOn(account, date),
        appointments: t.act?.appointments ?? 0,
        by_hour: t.act ? HOUR_BANDS.map((_, h) => t.act?.by_hour[h] ?? 0) : HOUR_BANDS.map(() => 0),
        calls: o?.talk_count ?? ZERO_OP.talk_count,
        reception_sec: reception,
        talk_sec: talk,
        after_sec: after,
        after_count: o?.after_count ?? 0,
        login_sec: login,
        break_sec: brk,
        away_sec: o?.away_sec ?? ZERO_OP.away_sec,
        shift_hours: sh && o ? sh.shift : null,
        break_hours: sh && o ? sh.brk : null,
        worked_hours: sh ? (o ? sh.worked : 0) : bbHours,
        hours_source: sh ? sh.source : 'bb',
        commute: i === 0 ? commute : 0,
        commute_base: i === 0 ? commuteBase : 0,
      };
      staff.push({ ...base, pay: referencePay(base, login) });
      trace[`${account.bb_id}|${t.company_id ?? ''}`] = {
        import_id: input.importId,
        op_report_rows: o ? [o.source_row] : [],
        activity_rows: t.act?.source_rows ?? [],
      };
    });
  }

  if (unknown.length > 0) {
    issues.push({
      level: policy === 'abort' ? 'error' : 'warning',
      code: 'unknown_bb_id',
      message: `オペレーターマスタに無いBB IDが ${unknown.length} 件あります: ${unknown.join('、')}`,
    });
  }
  if (noOp.length > 0) {
    issues.push({ level: 'warning', code: 'no_op_report', message: `OPレポートに無いBB IDに取得があります(稼働0・通話0で取り込みます): ${noOp.join('、')}` });
  }
  if (multi.length > 0) {
    issues.push({
      level: 'warning',
      code: 'multi_business',
      message: `同じBB IDが複数の業務に架電しています。OPレポートの稼働・通話回数は取得の多い業務の行に付けました(按分は未確定 P-19e): ${multi.join('、')}`,
    });
  }
  const unit_prices = Object.fromEntries(companies.map((c) => [c.id, priceOn(c, date)]));
  const hasActivity = activity.size > 0;
  const summary: DailySummary = {
    id: date,
    date,
    staff,
    unit_prices,
    note: hasActivity ? 'BlueBean取込(OPレポート＋対応履歴。報酬は参考値)' : 'OPレポート取込(取得件数は対応履歴の取込後。報酬は参考値)',
    sources: {},
  };
  return { summary, issues, trace };
}
